/**
 * Shared type definitions for Peacock CMS API
 *
 * This file contains common types used across multiple utility functions
 * that interact with the Peacock CMS API.
 */

/**
 * Peacock content node types
 *
 * @remarks
 * These enums represent the different types of nodes in the Peacock CMS content graph.
 * Use these constants instead of string literals for type safety and autocomplete.
 *
 * The Peacock CMS uses two main categories:
 * - **CATALOGUE/** types: Organizational/container nodes that group other content
 * - **ASSET/** types: Actual playable content (movies, episodes, clips, etc.)
 *
 * @example
 * ```typescript
 * if (asset.type === PeacockNodeType.Series) {
 *   // Handle series with seasons and episodes
 * } else if (asset.type === PeacockNodeType.Programme) {
 *   // Handle standalone movie/program
 * }
 * ```
 */
enum PeacockNodeType {
  // Catalogue types - organizational/container nodes
  /** Browse page container (e.g., "/home", "/movies", "/tv") */
  Group = "CATALOGUE/GROUP",
  /** Rail/carousel within a browse page containing multiple tiles */
  Collection = "CATALOGUE/COLLECTION",
  /** TV series container with multiple seasons and episodes */
  Series = "CATALOGUE/SERIES",
  /** Season container within a series holding episodes */
  Season = "CATALOGUE/SEASON",
  /** Dynamic pointer/reference to other content nodes (used for personalization) */
  Link = "CATALOGUE/LINK",
  /** Brand hub or themed collection page */
  Hub = "CATALOGUE/HUB",

  // Asset types - actual playable content
  /** Standalone movie or program (non-episodic content) */
  Programme = "ASSET/PROGRAMME",
  /** Individual episode within a season */
  Episode = "ASSET/EPISODE",
  /** Sports Live Event (live or recorded sporting events) */
  SLE = "ASSET/SLE",
  /** Short video clip or promotional content */
  Clip = "ASSET/CLIP",
  /** Linear TV time slot for scheduled programming */
  LinearSlot = "ASSET/LINEAR_SLOT",
}

/**
 * Image types available in Peacock CMS
 *
 * @remarks
 * These represent different aspect ratios and use cases for content imagery.
 * The naming convention follows a pattern:
 * - **34**: 3:4 aspect ratio (portrait/vertical orientation)
 * - **169**: 16:9 aspect ratio (landscape/horizontal orientation)
 * - **TitleArt**: Images with text/title overlay
 * - **NonTitleArt**: Images without text overlay (clean artwork)
 * - **Scene**: Screenshots or scene captures from the content
 *
 * @example
 * ```typescript
 * // Find a specific image type from an asset
 * const poster = asset.attributes.images.find(img => img.type === ImageType.TitleArt34);
 * const backdrop = asset.attributes.images.find(img => img.type === ImageType.Scene169);
 * ```
 */
enum ImageType {
  /** Standard landscape image (general purpose) */
  Landscape = "landscape",
  /** Title artwork in 3:4 portrait ratio (poster with text) */
  TitleArt34 = "titleArt34",
  /** Title artwork in 16:9 landscape ratio (banner with text) */
  TitleArt169 = "titleArt169",
  /** Clean artwork in 16:9 landscape ratio (no text overlay) */
  NonTitleArt169 = "nonTitleArt169",
  /** Clean artwork in 3:4 portrait ratio (no text overlay) */
  NonTitleArt34 = "nonTitleArt34",
  /** Background backdrop in 3:4 portrait ratio */
  Backdrop34 = "backdrop34",
  /** Transparent title logo overlay (PNG with alpha channel) */
  TitleLogo = "titleLogo",
  /** Scene capture/screenshot in 16:9 landscape ratio */
  Scene169 = "scene169",
  /** Scene capture/screenshot in 3:4 portrait ratio */
  Scene34 = "scene34",
  /** Brand hub focused state image */
  ConnectFocus = "connectFocus",
  /** Brand hub unfocused state image */
  ConnectUnfocus = "connectUnfocus",
  /** Background image for unfocused state in UI */
  BackgroundUnfocus = "backgroundUnfocus",
  /** Background image for focused state in UI */
  BackgroundFocus = "backgroundFocus",
  /** Background image for rail/carousel containers */
  RailBackground = "railBackground",
  /** Generic tile/thumbnail image */
  Tile = "tile",
  /** Small icon image */
  Icon = "icon",
}

/**
 * Template types for rail rendering
 *
 * @remarks
 * Defines how a rail/collection should be displayed in the UI.
 * Each template provides a different visual layout and interaction pattern.
 *
 * @example
 * ```typescript
 * if (rail.renderHint?.template === TemplateType.ContinueWatching) {
 *   // Render with progress bars and resume points
 * } else if (rail.renderHint?.template === TemplateType.Showcase) {
 *   // Render as large featured carousel
 * }
 * ```
 */
enum TemplateType {
  /** Featured/hero content at the top of a page */
  Highlight = "HIGHLIGHT",
  /** Vertical scrolling list layout */
  VerticalView = "VERTICAL_VIEW",
  /** Horizontal scrolling carousel layout */
  HorizontalView = "HORIZONTAL_VIEW",
  /** Continue watching rail with playback progress indicators */
  ContinueWatching = "CONTINUE_WATCHING",
  /** User's saved watchlist items */
  Watchlist = "WATCHLIST",
  /** Large showcase/featured carousel */
  Showcase = "SHOWCASE",
  /** Gaming-specific rail layout */
  Gamerail = "GAME_RAIL",
  /** Live and upcoming content rail */
  Live = "LIVE",
  /** Grid layout (multiple columns) */
  Grid = "GRID",
}

/**
 * Image template types for tile rendering
 *
 * @remarks
 * Specifies which image template strategy to use when displaying tiles.
 */
enum ImageTemplateType {
  /** Use the default image template for this rail type */
  Default = "DEFAULT",
  /** Override with a custom image template */
  Override = "override",
}

/**
 * Orientation for rail/tile presentation
 *
 * @remarks
 * Defines the aspect ratio and layout direction for content tiles.
 */
enum Orientation {
  /** Landscape/horizontal orientation (16:9 typical) */
  Landscape = "landscape",
  /** Portrait/vertical orientation (3:4 typical) */
  Portrait = "portrait",
}

/**
 * String boolean values used in API responses
 *
 * @remarks
 * The Peacock API uses string representations of booleans in some fields.
 * Use this enum for type-safe comparisons instead of string literals.
 *
 * @example
 * ```typescript
 * if (rail.renderHint?.autoPlay === StringBoolean.True) {
 *   // Auto-play is enabled
 * }
 * ```
 */
enum StringBoolean {
  /** Represents true/enabled state */
  True = "TRUE",
  /** Represents false/disabled state */
  False = "FALSE",
}

/**
 * Image asset returned from Peacock CMS API
 *
 * @remarks
 * Represents a single image resource with its URL and type classification.
 * Assets typically have multiple images of different types to support various
 * UI layouts and device resolutions.
 *
 * @example
 * ```typescript
 * // Find the poster image for a movie
 * const asset = await fetchAssetDetails("/movies/jurassic-world");
 * const poster = asset.attributes.images.find(img => img.type === "ImageType.titleArt34");
 * if (poster) {
 *   console.log(`Poster URL: ${poster.url}`);
 * }
 * ```
 */
interface ImageAsset {
  /** Full URL to the image resource */
  url: string;
  /** Type/classification of this image (determines aspect ratio and usage) */
  type: ImageType;
}

export {
  PeacockNodeType,
  ImageType,
  TemplateType,
  ImageTemplateType,
  Orientation,
  StringBoolean,
};
export type { ImageAsset };
